/**
 * Text operations over UTF-16 code units: the ot.js shape.
 *
 * An op covers the whole base text: a positive integer retains n code units,
 * a string inserts itself, a negative integer deletes n. Canonical form (as
 * built by OpBuilder): no zeros or empty inserts, adjacent components of one
 * kind merged, an insert before a delete where they meet.
 *
 * Reference implementation for the Swift and Kotlin ports, which run the same
 * `textOps.fixtures.json`. Change all three or none.
 */

export type TextOpComponent = number | string
export type TextOp = TextOpComponent[]
export type IndexAssoc = "before" | "after"

const isRetain = (c: TextOpComponent | undefined): c is number => typeof c === "number" && c > 0
const isDelete = (c: TextOpComponent | undefined): c is number => typeof c === "number" && c < 0
const isInsert = (c: TextOpComponent | undefined): c is string => typeof c === "string"

export class OpBuilder {
  ops: TextOp = []

  retain(n: number): this {
    if (n === 0) return this
    const last = this.ops.length - 1
    if (isRetain(this.ops[last])) this.ops[last] = (this.ops[last] as number) + n
    else this.ops.push(n)
    return this
  }

  insert(s: string): this {
    if (s === "") return this
    const ops = this.ops
    const last = ops.length - 1
    if (isInsert(ops[last])) {
      ops[last] = (ops[last] as string) + s
    } else if (isDelete(ops[last])) {
      // An insert goes before a delete where they meet.
      if (isInsert(ops[last - 1])) ops[last - 1] = (ops[last - 1] as string) + s
      else ops.splice(last, 0, s)
    } else {
      ops.push(s)
    }
    return this
  }

  delete(n: number): this {
    if (n === 0) return this
    const d = -Math.abs(n)
    const last = this.ops.length - 1
    if (isDelete(this.ops[last])) this.ops[last] = (this.ops[last] as number) + d
    else this.ops.push(d)
    return this
  }
}

export function baseLength(op: TextOp): number {
  let n = 0
  for (const c of op) if (typeof c === "number") n += Math.abs(c)
  return n
}

export function targetLength(op: TextOp): number {
  let n = 0
  for (const c of op) n += typeof c === "number" ? Math.max(c, 0) : c.length
  return n
}

export function identityOp(length: number): TextOp {
  return length > 0 ? [length] : []
}

export function isIdentityOp(op: TextOp): boolean {
  return op.every(isRetain)
}

/** The trust-boundary check: safe non-zero integers or non-empty strings. */
export function isWellFormedOp(value: unknown): value is TextOp {
  if (!Array.isArray(value)) return false
  return value.every(
    (c) => (typeof c === "number" && Number.isSafeInteger(c) && c !== 0) || (typeof c === "string" && c.length > 0),
  )
}

export function apply(text: string, op: TextOp): string {
  if (!isWellFormedOp(op)) throw new Error("Malformed text op")
  if (baseLength(op) !== text.length) {
    throw new Error(`Text op base length ${baseLength(op)} does not match text length ${text.length}`)
  }
  const parts: string[] = []
  let pos = 0
  for (const c of op) {
    if (isInsert(c)) parts.push(c)
    else if (c > 0) {
      parts.push(text.slice(pos, pos + c))
      pos += c
    } else pos -= c
  }
  return parts.join("")
}

/** `apply(t, compose(a, b)) === apply(apply(t, a), b)`. */
export function compose(a: TextOp, b: TextOp): TextOp {
  if (targetLength(a) !== baseLength(b)) throw new Error("Cannot compose: length mismatch")
  const out = new OpBuilder()
  let i = 0
  let j = 0
  let c1: TextOpComponent | undefined = a[i++]
  let c2: TextOpComponent | undefined = b[j++]
  for (;;) {
    if (c1 === undefined && c2 === undefined) break
    if (isDelete(c1)) {
      out.delete(c1)
      c1 = a[i++]
      continue
    }
    if (isInsert(c2)) {
      out.insert(c2)
      c2 = b[j++]
      continue
    }
    if (c1 === undefined || c2 === undefined) throw new Error("Cannot compose: op too short")
    if (isRetain(c1) && isRetain(c2)) {
      const n = Math.min(c1, c2)
      out.retain(n)
      c1 = c1 > n ? c1 - n : a[i++]
      c2 = c2 > n ? c2 - n : b[j++]
    } else if (isInsert(c1) && isDelete(c2)) {
      const n = Math.min(c1.length, -c2)
      c1 = c1.length > n ? c1.slice(n) : a[i++]
      c2 = -c2 > n ? c2 + n : b[j++]
    } else if (isInsert(c1) && isRetain(c2)) {
      const n = Math.min(c1.length, c2)
      out.insert(c1.slice(0, n))
      c1 = c1.length > n ? c1.slice(n) : a[i++]
      c2 = c2 > n ? c2 - n : b[j++]
    } else if (isRetain(c1) && isDelete(c2)) {
      const n = Math.min(c1, -c2)
      out.delete(n)
      c1 = c1 > n ? c1 - n : a[i++]
      c2 = -c2 > n ? c2 + n : b[j++]
    }
  }
  return out.ops
}

/**
 * `transform(a, b) → [a', b']` with `apply(apply(t, a), b') === apply(apply(t, b), a')`.
 * `b` is the accepted change: at a tie (both insert at one spot), `b`'s insert goes first.
 */
export function transform(a: TextOp, b: TextOp): [TextOp, TextOp] {
  if (baseLength(a) !== baseLength(b)) throw new Error("Cannot transform: base length mismatch")
  const aP = new OpBuilder()
  const bP = new OpBuilder()
  let i = 0
  let j = 0
  let c1: TextOpComponent | undefined = a[i++]
  let c2: TextOpComponent | undefined = b[j++]
  for (;;) {
    if (c1 === undefined && c2 === undefined) break
    if (isInsert(c2)) {
      aP.retain(c2.length)
      bP.insert(c2)
      c2 = b[j++]
      continue
    }
    if (isInsert(c1)) {
      aP.insert(c1)
      bP.retain(c1.length)
      c1 = a[i++]
      continue
    }
    if (c1 === undefined || c2 === undefined) throw new Error("Cannot transform: op too short")
    const n1 = Math.abs(c1)
    const n2 = Math.abs(c2)
    const n = Math.min(n1, n2)
    if (isRetain(c1) && isRetain(c2)) {
      aP.retain(n)
      bP.retain(n)
    } else if (isDelete(c1) && isRetain(c2)) {
      aP.delete(n)
    } else if (isRetain(c1) && isDelete(c2)) {
      bP.delete(n)
    }
    // Both delete: the text is gone on both sides, nothing to emit.
    c1 = n1 > n ? Math.sign(c1) * (n1 - n) : a[i++]
    c2 = n2 > n ? Math.sign(c2) * (n2 - n) : b[j++]
  }
  return [aP.ops, bP.ops]
}

/**
 * Moves index `i` of the base text through `op`. An insert exactly at `i`
 * shifts it only for 'after'; an index inside a deleted range collapses to its start.
 */
export function transformIndex(i: number, op: TextOp, assoc: IndexAssoc = "after"): number {
  let pos = 0
  let result = i
  for (const c of op) {
    if (pos > i) break
    if (isInsert(c)) {
      if (pos < i || assoc === "after") result += c.length
    } else if (c > 0) {
      pos += c
    } else {
      const n = -c
      result -= Math.min(n, i - pos)
      pos += n
    }
  }
  return result
}

/** The op that undoes `op` applied to `text`. */
export function invert(op: TextOp, text: string): TextOp {
  const out = new OpBuilder()
  let pos = 0
  for (const c of op) {
    if (isInsert(c)) out.delete(c.length)
    else if (c > 0) {
      out.retain(c)
      pos += c
    } else {
      out.insert(text.slice(pos, pos - c))
      pos -= c
    }
  }
  return out.ops
}

const isHigh = (u: number) => u >= 0xd800 && u <= 0xdbff
const isLow = (u: number) => u >= 0xdc00 && u <= 0xdfff

/** True when a retain or delete boundary falls between the halves of a surrogate pair. */
export function splitsSurrogate(text: string, op: TextOp): boolean {
  let pos = 0
  for (const c of op) {
    if (typeof c !== "number") continue
    pos += Math.abs(c)
    if (pos > 0 && pos < text.length && isHigh(text.charCodeAt(pos - 1)) && isLow(text.charCodeAt(pos))) return true
  }
  return false
}

/** FNV-1a 32 over UTF-16 code units, 8 lowercase hex digits. */
export function hashText(text: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h.toString(16).padStart(8, "0")
}

function codePoints(text: string): string[] {
  const out: string[] = []
  for (let i = 0; i < text.length; i++) {
    const u = text.charCodeAt(i)
    if (isHigh(u) && i + 1 < text.length && isLow(text.charCodeAt(i + 1))) {
      out.push(text.slice(i, i + 2))
      i++
    } else out.push(text[i])
  }
  return out
}

/**
 * The op turning `a` into `b`, never cutting a surrogate pair: trim the common
 * prefix and suffix by code point, then greedy Myers on the middle. Above
 * `maxEditDistance`, insert the new middle and delete the old one.
 */
// ponytail: O(N·D) time and O(D²) memory for the trace; the cap bounds both. A
// line-mode pre-pass is the upgrade if long rewrites ever show up in profiles.
export function diff(a: string, b: string, { maxEditDistance = 2000 }: { maxEditDistance?: number } = {}): TextOp {
  const x = codePoints(a)
  const y = codePoints(b)
  let start = 0
  while (start < x.length && start < y.length && x[start] === y[start]) start++
  let endX = x.length
  let endY = y.length
  while (endX > start && endY > start && x[endX - 1] === y[endY - 1]) {
    endX--
    endY--
  }
  const prefix = x.slice(0, start).join("").length
  const suffix = x.slice(endX).join("").length
  const oldMid = x.slice(start, endX)
  const newMid = y.slice(start, endY)

  const out = new OpBuilder().retain(prefix)
  const script = myers(oldMid, newMid, maxEditDistance)
  if (script === null) {
    out.insert(newMid.join("")).delete(oldMid.join("").length)
  } else {
    for (const [kind, token] of script) {
      if (kind === "=") out.retain(token.length)
      else if (kind === "+") out.insert(token)
      else out.delete(token.length)
    }
  }
  return out.retain(suffix).ops
}

type Edit = ["=" | "+" | "-", string]

function myers(x: string[], y: string[], cap: number): Edit[] | null {
  const n = x.length
  const m = y.length
  const limit = Math.min(n + m, cap)
  const off = limit + 1
  const v = new Array<number>(2 * limit + 3).fill(0)
  // trace[d] holds V[-d-1 .. d+1] as it was before step d.
  const trace: number[][] = []
  const down = (vk: (k: number) => number, k: number, d: number) => k === -d || (k !== d && vk(k - 1) < vk(k + 1))
  for (let d = 0; d <= limit; d++) {
    trace.push(v.slice(off - d - 1, off + d + 2))
    for (let k = -d; k <= d; k += 2) {
      let px = down((kk) => v[off + kk], k, d) ? v[off + k + 1] : v[off + k - 1] + 1
      let py = px - k
      while (px < n && py < m && x[px] === y[py]) {
        px++
        py++
      }
      v[off + k] = px
      if (px >= n && py >= m) return backtrack(x, y, trace, d, down)
    }
  }
  return null
}

function backtrack(
  x: string[],
  y: string[],
  trace: number[][],
  dEnd: number,
  down: (vk: (k: number) => number, k: number, d: number) => boolean,
): Edit[] {
  const edits: Edit[] = []
  let px = x.length
  let py = y.length
  for (let d = dEnd; d > 0; d--) {
    const t = trace[d]
    const vk = (k: number) => t[k + d + 1]
    const k = px - py
    const prevK = down(vk, k, d) ? k + 1 : k - 1
    const prevX = vk(prevK)
    const prevY = prevX - prevK
    while (px > prevX && py > prevY) {
      edits.push(["=", x[--px] as string])
      py--
    }
    if (px === prevX) edits.push(["+", y[prevY] as string])
    else edits.push(["-", x[prevX] as string])
    px = prevX
    py = prevY
  }
  while (px > 0) {
    edits.push(["=", x[--px] as string])
    py--
  }
  return edits.reverse()
}
